import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'

type PlatformIntegration = {
  id: string
  connected: boolean
}

type PlatformIntegrationsResponse = {
  platform: PlatformIntegration[]
}

/**
 * Platform-admin Integrations page (`/admin/integrations`).
 *
 * Read-only status board for Autoflo's platform infrastructure
 * (Clerk, Neon, Sentry, Anthropic, Vapi, Twilio, R2, CF Email + the
 * BYOK credential-encryption KEK). Tenant supplier integrations
 * (WorldPac etc.) live on the tenant `/settings/integrations` tab —
 * never here.
 *
 * Gated by `<RequirePlatformAdmin>` in App.tsx; the Worker also gates
 * `/api/admin/*` server-side via `requirePlatformAdmin()`.
 */
export function AdminIntegrations() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()

  const query = useQuery({
    queryKey: ['admin', 'integrations'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/admin/integrations', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as PlatformIntegrationsResponse
    },
  })

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-foreground">
          {t('header.title')}
        </h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t('header.subtitle')}
        </p>
      </div>

      <Card className="p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('integrations.title')}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('integrations.subtitle')}
        </p>

        {query.isError ? (
          <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {query.error instanceof Error
              ? query.error.message
              : t('integrations.error')}
          </div>
        ) : null}

        {query.isLoading ? (
          <div className="mt-4 text-sm text-muted-foreground">
            {tCommon('loading')}
          </div>
        ) : null}

        {query.data ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {query.data.platform.map((integration) => (
              <div
                key={integration.id}
                className="flex flex-row items-center justify-between gap-3 rounded-lg border border-border bg-background p-4"
              >
                <div className="min-w-0">
                  <div className="font-medium text-foreground">
                    {t(`integrations.list.${integration.id}.name`)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t(`integrations.list.${integration.id}.description`)}
                  </div>
                </div>
                <span
                  className={[
                    'shrink-0 rounded-full px-2 py-1 text-xs font-semibold',
                    integration.connected
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-muted text-muted-foreground',
                  ].join(' ')}
                >
                  {integration.connected
                    ? t('integrations.connected')
                    : t('integrations.not_connected')}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </Card>
    </div>
  )
}
